import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { fetchTelephonyIntakeHealth } from '../../api';
import { PANEL_IDS } from '../../config/panelRegistry';
import { usePanelSettings } from '../../contexts/PanelSettingsContext';
import useTimeZone from '../../hooks/useTimeZone';
import InfoHint from '../InfoHint';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { INTAKE_HEALTH_POLL_MS, buildIntakeHealthView, shouldPoll } from './telephonyIntakeHealth';

const STATUS_BADGE_CLASSES = {
  ok: 'bg-emerald-600 hover:bg-emerald-600 text-white',
  warning: 'bg-amber-500 hover:bg-amber-500 text-white',
  error: 'bg-red-600 hover:bg-red-600 text-white',
  idle: 'bg-gray-200 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-700 dark:text-gray-100'
};

const NOTICE_CLASSES = {
  error: 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200',
  warning: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200',
  muted: 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800/40 dark:text-gray-300'
};

const describeError = (error) =>
  error?.response?.data?.message || error?.message || 'Неизвестная ошибка';

const Notice = ({ tone, children, ...rest }) => (
  <div
    className={`flex items-start gap-3 rounded-md border p-3 text-sm ${NOTICE_CLASSES[tone] ?? NOTICE_CLASSES.muted}`}
    {...rest}
  >
    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
    <div className="space-y-1">{children}</div>
  </div>
);

const TelephonyBlock = ({ telephony }) => (
  <section data-telephony={telephony.key} className="space-y-3 rounded-md border border-gray-200 p-4 dark:border-gray-700">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="font-medium text-gray-900 dark:text-white">{telephony.name}</h3>
      <Badge data-status={telephony.status.tone} className={STATUS_BADGE_CLASSES[telephony.status.tone]}>
        {telephony.status.label}
      </Badge>
    </div>

    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
      {telephony.facts.map((fact) => (
        <div key={fact.id} data-fact={fact.id}>
          <dt className="text-gray-500 dark:text-gray-400">{fact.label}</dt>
          <dd className="text-gray-900 dark:text-white">{fact.value}</dd>
        </div>
      ))}
    </dl>

    {telephony.rejections.length > 0 && (
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-900 dark:text-white">Отказы за сутки</p>
        {telephony.rejections.map((rejection) => (
          <Notice key={rejection.reason} tone={rejection.tone} data-rejection={rejection.reason}>
            <p>
              <span className="font-medium">{rejection.label}</span>: {rejection.count}
              {rejection.lastAt ? `, последний — ${rejection.lastAt}` : ''}
            </p>
            <p className="text-xs opacity-90">{rejection.hint}</p>
          </Notice>
        ))}
      </div>
    )}

    {telephony.problems.map((problem) => (
      <Notice key={problem.id} tone={problem.tone} data-problem={problem.id}>
        <p className="font-medium">{problem.text}</p>
        <p className="text-xs opacity-90">{problem.hint}</p>
      </Notice>
    ))}
  </section>
);

/**
 * Принимает ли стенд звонки от телефонии — для администратора, без SSH на сервер. Видимость и роль
 * проверяет родитель: карточка опрашивает сервер по таймеру, и `return null` после хуков таймер бы не
 * остановил.
 */
const TelephonyIntakeHealthCard = () => {
  const panels = usePanelSettings();
  const { timeZone } = useTimeZone();
  const [response, setResponse] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Состояние меняется только после ответа: эффект запускает загрузку, а не перерисовку.
  const load = useCallback(async () => {
    try {
      setResponse(await fetchTelephonyIntakeHealth());
      setLoadError(null);
    } catch (error) {
      setLoadError(describeError(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Фоновая вкладка не опрашивает: каждый запрос — проход по сводкам звонков за сутки.
    const timer = setInterval(() => {
      if (shouldPoll(document.visibilityState)) {
        load();
      }
    }, INTAKE_HEALTH_POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const handleRefresh = () => {
    setIsLoading(true);
    load();
  };

  const view = useMemo(() => buildIntakeHealthView(response, { timeZone }), [response, timeZone]);

  return (
    <Card data-panel-id={PANEL_IDS['ui:integrations.telephony-health']}>
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div className="flex items-center gap-1.5">
          <CardTitle>{panels.title(PANEL_IDS['ui:integrations.telephony-health'])}</CardTitle>
          <InfoHint
            text={panels.description(PANEL_IDS['ui:integrations.telephony-health'])}
            label="О карточке"
            trackId="integrations.telephony_health.hint"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          data-ev="integrations.telephony_health.refresh"
          onClick={handleRefresh}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Обновить
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        {loadError && (
          <Notice tone="error" data-intake-error="">
            <p>Не удалось загрузить состояние приёма: {loadError}</p>
          </Notice>
        )}

        {view.reporter && (
          <Notice tone={view.reporter.tone} data-reporter="">
            <p>{view.reporter.text}</p>
          </Notice>
        )}

        {response && view.isEmpty && !view.reporter && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            На сервере не настроена ни одна телефония.
          </p>
        )}

        {view.telephonies.map((telephony) => (
          <TelephonyBlock key={telephony.key} telephony={telephony} />
        ))}
      </CardContent>
    </Card>
  );
};

export default TelephonyIntakeHealthCard;
